import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Utensils, Sparkles, ShoppingCart, Save, Trash2 } from "lucide-react";
import { api } from "../api/client";

const MEAL_ORDER = ["breakfast", "lunch", "dinner", "snack"];

const DietGenerator = ({ token }) => {
  const [kcal, setKcal] = useState(1800);
  const [dietType, setDietType] = useState("redukcyjna");
  const [days, setDays] = useState(1);
  const [thermomix, setThermomix] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("__none__");
  const [saved, setSaved] = useState([]);

  const loadSaved = async () => {
    try { const r = await api("get", "/diet/plans", null, token); setSaved(r.data || []); } catch { /* noop */ }
  };
  useEffect(() => {
    api("get", "/trainer/clients", null, token).then((r) => setClients(r.data || [])).catch(() => {});
    loadSaved();
    /* eslint-disable-next-line */
  }, []);

  const save = async () => {
    if (!plan) return;
    try {
      const r = await api("post", "/diet/plans", {
        client_id: client !== "__none__" ? client : null, diet_type: plan.diet_type,
        target_calories: plan.target_calories, days: plan.days, shopping_list: plan.shopping_list,
      }, token);
      toast.success(r.data.client_id ? "Dieta zapisana i przypisana klientowi" : "Dieta zapisana (szkic)");
      loadSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zapisać");
    }
  };

  const removeSaved = async (id) => {
    try { await api("delete", `/diet/plans/${id}`, null, token); loadSaved(); }
    catch (err) { toast.error("Nie udało się usunąć"); }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api("post", "/diet/generate",
        { target_calories: Number(kcal), diet_type: dietType, days: Number(days), thermomix }, token);
      setPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wygenerować diety");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="diet-generator">
      <Card>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Utensils size={20} /><h3 style={{ margin: 0 }}>Generator diety</h3>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="field" style={{ maxWidth: 140 }}>
              <Label>Kalorie</Label>
              <Input type="number" value={kcal} onChange={(e) => setKcal(e.target.value)} step={50} />
            </div>
            <div className="field" style={{ minWidth: 180 }}>
              <Label>Typ diety</Label>
              <Select value={dietType} onValueChange={setDietType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="redukcyjna">Redukcyjna</SelectItem>
                  <SelectItem value="stabilizacja">Stabilizacja</SelectItem>
                  <SelectItem value="masa">Masa</SelectItem>
                  <SelectItem value="sport">Sport</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field" style={{ minWidth: 120 }}>
              <Label>Liczba dni</Label>
              <Select value={String(days)} onValueChange={setDays}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 dzień</SelectItem>
                  <SelectItem value="3">3 dni</SelectItem>
                  <SelectItem value="5">5 dni</SelectItem>
                  <SelectItem value="7">7 dni</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer", paddingBottom: 8 }}>
              <input type="checkbox" checked={thermomix} onChange={(e) => setThermomix(e.target.checked)} />
              Przepisy Thermomix
            </label>
            <Button onClick={generate} disabled={loading}>
              <Sparkles size={16} /> {loading ? "Generuję…" : "Generuj dietę"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {plan && (
        <Card style={{ marginTop: 12 }}>
          <CardContent style={{ padding: 16, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="field" style={{ minWidth: 220 }}>
              <Label>Przypisz klientowi (opcjonalnie)</Label>
              <Select value={client} onValueChange={setClient}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— szkic (bez klienta) —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={save}><Save size={16} /> Zapisz dietę</Button>
          </CardContent>
        </Card>
      )}

      {plan && (plan.days || []).map((day) => (
        <Card key={day.day} style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>Dzień {day.day}</h4>
              <span style={{ fontWeight: 700 }}>
                {day.totals.calories} kcal · B {day.totals.protein} · W {day.totals.carbs} · T {day.totals.fat}
              </span>
            </div>
            {[...day.meals].sort((a, b) => MEAL_ORDER.indexOf(a.meal_type) - MEAL_ORDER.indexOf(b.meal_type)).map((m, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border,#eee)" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>{m.meal_label}: {m.title}{m.device === "thermomix" ? " 🍲 TM" : ""}</strong>
                  <span style={{ opacity: 0.7 }}>{m.calories} kcal</span>
                </div>
                <div style={{ fontSize: 12, opacity: 0.6 }}>B {m.protein} · W {m.carbs} · T {m.fat}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>{(m.ingredients || []).join(", ")}</div>
                {(m.instructions || []).length > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ fontSize: 12, cursor: "pointer", color: "var(--primary, #10b981)", fontWeight: 600 }}>
                      {m.device === "thermomix" ? "🍲 Kroki Thermomix" : "Przygotowanie"} ({m.instructions.length})
                    </summary>
                    <ol style={{ fontSize: 12, margin: "6px 0 0 18px", opacity: 0.85, lineHeight: 1.5 }}>
                      {m.instructions.map((s, si) => <li key={si} style={{ marginBottom: 3 }}>{s}</li>)}
                    </ol>
                  </details>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {plan?.shopping_list?.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <ShoppingCart size={18} />
              <h4 style={{ margin: 0 }}>Lista zakupów ({plan.days.length} {plan.days.length === 1 ? "dzień" : "dni"})</h4>
            </div>
            <div style={{ columns: "2 220px", columnGap: 28 }}>
              {plan.shopping_list.map((grp, gi) => (
                <div key={gi} style={{ breakInside: "avoid", marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.7, margin: "4px 0 4px" }}>
                    {grp.department}
                  </div>
                  {grp.items.map((it, i) => (
                    <div key={i} style={{ fontSize: 13, padding: "2px 0" }}>
                      • {it.name}{it.amount != null ? ` — ${it.amount} ${it.unit}` : ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {saved.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <h4 style={{ marginTop: 0 }}>Zapisane diety ({saved.length})</h4>
            {saved.map((s) => {
              const cname = clients.find((c) => c.id === s.client_id)?.name;
              return (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border,#eee)" }}>
                  <div>
                    <strong>{s.diet_type}</strong> · {s.target_calories} kcal · {s.days?.length || 0} dni
                    <div style={{ fontSize: 12, opacity: 0.6 }}>
                      {s.status === "assigned" ? `Klient: ${cname || s.client_id}` : "Szkic"} · {(s.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeSaved(s.id)}><Trash2 size={16} /></Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DietGenerator;
